import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Breadcrumbs,
  Card,
  Center,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  TypographyStylesProvider,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBook,
  IconBookmark,
  IconClock,
  IconShare,
  IconUser,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { notifications } from '@mantine/notifications';
import { fetchOne } from '@/shared/api/jsonapi/client';
import { EmptyState } from '@/shared/components/EmptyState';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Article extends Denormalized {
  type: 'articles';
  title?: string;
  excerpt?: string;
  body?: string;
  category?: string;
  publishedAt?: string;
  authorName?: string;
  tags?: string[];
  coverUrl?: string;
}

export const Route = createFileRoute('/_app/articles/$id')({ component: ArticleDetail });

const CATEGORY_COLORS: Record<string, string> = {
  guida: 'blue',
  novità: 'grape',
  policy: 'orange',
  faq: 'teal',
  tecnico: 'indigo',
};

const BOOKMARKS_KEY = 'articles-bookmarks';

function loadBookmarks(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(BOOKMARKS_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

function saveBookmarks(set: Set<string>) {
  try { localStorage.setItem(BOOKMARKS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

function readingTime(body?: string, excerpt?: string): number {
  const text = (body ?? excerpt ?? '').replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return 1;
  return Math.max(1, Math.round(words / 220));
}

function ArticleDetail() {
  const { id } = useParams({ from: '/_app/articles/$id' });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['article', id],
    queryFn: () => fetchOne<Article>('articles', id),
  });

  const [bookmarked, setBookmarked] = useState(false);
  useEffect(() => { setBookmarked(loadBookmarks().has(id)); }, [id]);

  if (isLoading) {
    return <Center mt="xl"><Loader /></Center>;
  }
  if (isError || !data) {
    return (
      <Center mt="xl">
        <Stack align="center" gap="sm">
          <ThemeIcon variant="light" color="gray" size="xl" radius="xl"><IconBook size={28} /></ThemeIcon>
          <Text c="dimmed">Articolo non disponibile.</Text>
          <Anchor component={Link} to="/articles">Torna alla lista</Anchor>
        </Stack>
      </Center>
    );
  }

  const article = data;
  const minutes = readingTime(article.body, article.excerpt);
  const color = CATEGORY_COLORS[(article.category ?? '').toLowerCase()] ?? 'gray';

  const toggleBookmark = () => {
    const set = loadBookmarks();
    if (set.has(id)) set.delete(id); else set.add(id);
    saveBookmarks(set);
    setBookmarked(set.has(id));
  };

  const share = async () => {
    const url = window.location.href;
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: article.title ?? 'Articolo',
          url,
        });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      notifications.show({ color: 'green', message: 'Link copiato' });
    } catch {
      notifications.show({ color: 'orange', message: 'Impossibile copiare il link' });
    }
  };

  const safeBody = article.body ? DOMPurify.sanitize(article.body) : '';

  return (
    <Stack maw={840} mx="auto">
      <Breadcrumbs>
        <Anchor component={Link} to="/articles" size="sm">
          <Group gap={4}><IconArrowLeft size={14} /> Articoli</Group>
        </Anchor>
        {article.category && (
          <Text size="sm" c="dimmed">{article.category}</Text>
        )}
      </Breadcrumbs>

      {article.coverUrl ? (
        <img
          src={article.coverUrl}
          alt=""
          loading="lazy"
          style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 12 }}
        />
      ) : (
        <div
          style={{
            height: 120,
            background: `linear-gradient(135deg, light-dark(var(--mantine-color-${color}-2), var(--mantine-color-${color}-9)), light-dark(var(--mantine-color-${color}-5), var(--mantine-color-${color}-7)))`,
            borderRadius: 12,
          }}
        />
      )}

      <Stack gap={4}>
        <Group>
          {article.category && (
            <Badge variant="light" color={color}>{article.category}</Badge>
          )}
          <Group gap={4}>
            <IconClock size={12} style={{ opacity: 0.6 }} />
            <Text size="xs" c="dimmed">{minutes} min di lettura</Text>
          </Group>
        </Group>
        <Title order={1}>{article.title ?? '(senza titolo)'}</Title>
        {article.excerpt && <Text c="dimmed">{article.excerpt}</Text>}
        <Group justify="space-between" mt="xs">
          <Group gap="xs">
            <IconUser size={14} style={{ opacity: 0.6 }} />
            <Text size="sm">{article.authorName ?? '—'}</Text>
            <Text size="sm" c="dimmed">
              · {article.publishedAt ? dayjs(article.publishedAt).format('DD MMM YYYY') : ''}
            </Text>
          </Group>
          <Group gap={4}>
            <Tooltip label={bookmarked ? 'Rimuovi dai segnalibri' : 'Aggiungi ai segnalibri'}>
              <ActionIcon
                variant={bookmarked ? 'filled' : 'light'}
                color={bookmarked ? 'yellow' : 'gray'}
                onClick={toggleBookmark}
                aria-label="Segnalibro"
              >
                <IconBookmark size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Condividi">
              <ActionIcon variant="light" onClick={() => void share()} aria-label="Condividi">
                <IconShare size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Stack>

      <Card withBorder padding="lg" radius="md">
        <ScrollArea>
          {safeBody ? (
            <TypographyStylesProvider>
              <div dangerouslySetInnerHTML={{ __html: safeBody }} />
            </TypographyStylesProvider>
          ) : (
            <EmptyState title="Nessun contenuto" description="L'articolo è ancora in bozza." py="md" />
          )}
        </ScrollArea>
      </Card>

      {article.tags && article.tags.length > 0 && (
        <Group gap={6}>
          {article.tags.map((t) => (
            <Badge key={t} size="sm" variant="dot" color="gray">{t}</Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}
